import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  loginAsync,
  selectUserInfo,
  selectError,
  clearError,
} from "@/store/redux/slices/auth/auth_slice";
import Button from "@/components/common/Button";
import OAuthButtons from "@/components/shared/OAuth_Buttons/OAuth_Buttons";
import { useLoader } from "@/hooks/LoaderProvider";
import Input from "@/components/common/inputs/Input";
import { ArrowRightOutlined } from "@/config/antd";
import Error from "@/components/common/alerts/Error";
import WebIcon from "@/components/common/Web_Icons";
import Logo from "@/components/common/Logo";
import { getLoginErrorContent } from "@/helper/error_handling";

const PasswordSignIn = () => {
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  
  const userInfo = useSelector(selectUserInfo) || {};
  const loginError = useSelector(selectError);

  // Use email from Redux state, fallback to location state, then empty string
  const email = userInfo?.email || location?.state?.email || "";

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  useEffect(() => {
    setIsActive(password.trim() !== "");
  }, [password]);

  // redirect them back to the email sign-in step. This avoids undefined state usage.
  useEffect(() => {
    if (!email) {
      navigate("/signin");
    }
  }, [email, navigate]);

  const handleProceed = useCallback(async () => {
    showLoader();
    // Clear any previous errors before attempting login
    dispatch(clearError());
    try {
      const resultAction = await dispatch(loginAsync({ email, password }));
      if (loginAsync.fulfilled.match(resultAction)) {
        // Login returns only { message, userID }. Full auth (tokens + user data)
        // happens after OTP verification on the 2FA screen.
        navigate("/2fa/verify");
      } else {
        setIsErrorVisible(true);
      }
    } finally {
      hideLoader();
    }
  }, [email, password, navigate, hideLoader, showLoader, dispatch]);

  const handleCloseError = () => {
    setIsErrorVisible(false);
    dispatch(clearError());
  };

  const handleForgotPassword = () => {
    showLoader();
    setTimeout(() => {
      navigate("/account-recovery/password-recovery");
      hideLoader();
    }, 2000);
  };

  const errorContent = getLoginErrorContent(loginError);

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "-100%" }}
      transition={{ type: "tween", ease: "anticipate", duration: 0.5 }}
      className="flex flex-col h-full md:justify-center">
      <div className="flex flex-col flex-1 p-4 pt-2 md:pt-4 mt-[-15rem] md:mt-auto">
        <div className="flex flex-col flex-1">
          <div className="flex items-center mb-2">
            <Logo name="VeegilLogo" size="small" />
            <h1 className="ml-2 font-bold heading-large text-primary">
              Sign in
            </h1>
          </div>

          <div>
            <p className="mb-6 text-left text-darkGrey text-small">
              Welcome to our service. We&apos;re thrilled that you&apos;re
              interested in using our service.
            </p>
          </div>

          <div className="mb-4">
            <Input
              data-testid="password-input"
              label="PASSWORD"
              placeholder="Enter your password"
              icon={<WebIcon icon="Lock" size="small" />}
              type="password"
              value={password}
              onChange={handlePasswordChange}
              id="password"
            />
          </div>

          <div>
            <Button
              data-testid="proceed-button"
              text="Proceed"
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              disabled={!password}
              isActive={isActive}
              color="primary"
              className="w-full"
            />
          </div>

          <div className="mt-4 text-left">
            <Link
              to="#"
              onClick={handleForgotPassword}
              className="font-medium underline text-primary text-small">
              Forgot your Password?
            </Link>
          </div>

          <div className="mt-6 text-left">
            <p className="mb-4 font-medium text-black text-small">
              Sign in using
            </p>
            {/* OAuth Buttons */}
            <OAuthButtons />
          </div>

          <div className="mt-6 text-left">
            <p className="text-small text-darkGrey whitespace-nowrap">
              Don&apos;t have a Veegil Account yet?{" "}
              <Link
                to="/signup"
                className="font-medium text-primary hover:underline">
                Sign up now
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header={errorContent.header}
        paragraph={errorContent.paragraph}
        buttonText={errorContent.buttonText}
        recoveryPath={errorContent.recoveryPath}
      />
    </motion.div>
  );
};

export default PasswordSignIn;
